import React, { useMemo, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Checkbox, Chip, FormControl, FormControlLabel,
  InputLabel, MenuItem, Paper, Select, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, TableSortLabel, TextField, Tooltip, Typography,
} from '@mui/material';
import { ExpandMore, Check, Close, Download, GroupAdd } from '@mui/icons-material';
import { orderProcessorAPI } from '../../services/api';
import { downloadBlob, errMsg, fmtDateTime, inr, isoDaysAgo, num, packCount, StatusChip, stripsFor } from './common';

interface Props {
  run: any;
  editable: boolean;
  onChanged: () => Promise<void> | void;
  onNext: () => void;
}

const PanelTable: React.FC<{ rows: any[]; cols: string[] }> = ({ rows, cols }) => (
  <TableContainer sx={{ maxHeight: 320 }}>
    <Table size="small" stickyHeader>
      <TableHead><TableRow>{cols.map((c) => <TableCell key={c}>{c}</TableCell>)}</TableRow></TableHead>
      <TableBody>
        {rows.map((r, i) => (
          <TableRow key={i}>{cols.map((c) => <TableCell key={c}>{typeof r[c] === 'number' ? num(r[c]) : String(r[c] ?? '')}</TableCell>)}</TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

type SortKey = 'medicine' | 'pack' | 'category' | 'stock' | 'avg' | 'dos' | 'bills' | 'landing' | 'optimizer' | 'customer' | 'order' | 'decision';

const numOrNull = (v: any) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const SORT_VALUE: Record<SortKey, (i: any) => string | number | null> = {
  medicine: (i) => String(i.medicine_name || '').toLowerCase(),
  pack: (i) => (i.pack_size && i.pack_size !== 'nan' ? String(i.pack_size).toLowerCase() : null),
  category: (i) => String(i.category_label || '').replace(/[^\p{L}\p{N}\s]/gu, '').trim().toLowerCase(),
  stock: (i) => numOrNull(i.current_stock),
  avg: (i) => numOrNull(i.avg_daily_sales),
  dos: (i) => numOrNull(i.days_of_stock),
  bills: (i) => numOrNull(i.bill_count),
  landing: (i) => numOrNull(i.landing_price),
  optimizer: (i) => (i.in_optimizer ? numOrNull(i.optimizer_qty ?? i.suggested_qty) : null),
  customer: (i) => (i.in_customer_orders ? numOrNull(i.customer_qty_units) : null),
  order: (i) => numOrNull(i.approved_qty),
  decision: (i) => String(i.decision || ''),
};

/** Item suggested by the optimizer AND ordered by customers — its total needs a reviewer decision. */
const isBoth = (i: any) => i.in_customer_orders && i.in_optimizer && Number(i.optimizer_qty ?? i.suggested_qty) > 0;

const ReviewStep: React.FC<Props> = ({ run, editable, onChanged, onNext }) => {
  const [category, setCategory] = useState('all');
  const [decision, setDecision] = useState('all');
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [source, setSource] = useState<'all' | 'optimizer' | 'customer' | 'both'>('all');
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' } | null>(null);
  const [coFrom, setCoFrom] = useState(run.customer_orders_info?.date_from || isoDaysAgo(7));
  const [coTo, setCoTo] = useState(run.customer_orders_info?.date_to || isoDaysAgo(0));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [rejectReason, setRejectReason] = useState('');
  const [qtyDraft, setQtyDraft] = useState<Record<number, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const budget = run.budget_info || {};
  // Budget-cut panel: add current stock / velocity / pack from the run's own rows so a cut can be judged
  const budgetTrimmedRows = useMemo(() => {
    const byName = new Map<string, any>();
    run.items.forEach((i: any) => byName.set(`${i.manufacturer_name}\u0000${i.medicine_name}`, i));
    return (run.budget_trimmed || []).map((t: any) => {
      const it = byName.get(`${t.manufacturer_name}\u0000${t.medicine_name}`);
      return {
        ...t,
        Pack: it?.pack_size && it.pack_size !== 'nan' ? it.pack_size : '',
        'Current Stock': it ? Number(it.current_stock) : '',
        'Avg/day': it?.avg_daily_sales !== null && it?.avg_daily_sales !== undefined ? Number(Number(it.avg_daily_sales).toFixed(2)) : '',
        'Days of Stock': it?.days_of_stock !== null && it?.days_of_stock !== undefined ? Number(Number(it.days_of_stock).toFixed(1)) : '',
      };
    });
  }, [run.items, run.budget_trimmed]);
  const skippedMinmaxRows = useMemo(() => {
    const byName = new Map<string, any>();
    run.items.forEach((i: any) => byName.set(`${i.manufacturer_name}\u0000${i.medicine_name}`, i));
    return (run.skipped_minmax || []).map((t: any) => {
      const it = byName.get(`${t.manufacturer_name}\u0000${t.medicine_name}`);
      return {
        ...t,
        Pack: it?.pack_size && it.pack_size !== 'nan' ? it.pack_size : '',
        'Avg/day': it?.avg_daily_sales !== null && it?.avg_daily_sales !== undefined ? Number(Number(it.avg_daily_sales).toFixed(2)) : '',
      };
    });
  }, [run.items, run.skipped_minmax]);
  const candidates = useMemo(
    () => run.items.filter((i: any) => showAll || Number(i.suggested_qty) > 0 || i.added_manually || i.in_customer_orders || i.decision === 'accepted'),
    [run.items, showAll],
  );
  const categories = useMemo(() => Array.from(new Set<string>(candidates.map((i: any) => i.category_label))).sort(), [candidates]);
  const filtered = useMemo(() => candidates.filter((i: any) =>
    (category === 'all' || i.category_label === category)
    && (source === 'all'
      || (source === 'both' && isBoth(i))
      || (source === 'optimizer' && i.in_optimizer && Number(i.optimizer_qty ?? i.suggested_qty) > 0)
      || (source === 'customer' && i.in_customer_orders))
    && (decision === 'all' || i.decision === decision)
    && (!search || `${i.medicine_name} ${i.manufacturer_name}`.toLowerCase().includes(search.toLowerCase()))),
  [candidates, category, decision, search, source]);
  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const get = SORT_VALUE[sort.key];
    const mul = sort.dir === 'asc' ? 1 : -1;
    return filtered.map((r: any, idx: number) => ({ r, idx, v: get(r) })).sort((a: any, b: any) => {
      if (a.v === null && b.v === null) return a.idx - b.idx;
      if (a.v === null) return 1;
      if (b.v === null) return -1;
      if (a.v < b.v) return -mul;
      if (a.v > b.v) return mul;
      return a.idx - b.idx;
    }).map((x: any) => x.r);
  }, [filtered, sort]);
  const pageRows = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const toggleSort = (key: SortKey) => {
    setSort((cur) => (cur && cur.key === key ? { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
    setPage(0);
  };
  const SortHead: React.FC<{ k: SortKey; label: React.ReactNode; align?: 'right'; minWidth?: number }> = ({ k, label, align, minWidth }) => (
    <TableCell align={align} sx={minWidth ? { minWidth } : undefined} sortDirection={sort?.key === k ? sort.dir : false}>
      <TableSortLabel active={sort?.key === k} direction={sort?.key === k ? sort.dir : 'asc'} onClick={() => toggleSort(k)}>{label}</TableSortLabel>
    </TableCell>
  );

  const accepted = run.items.filter((i: any) => i.decision === 'accepted');
  const pendingToOrder = run.items.filter((i: any) => i.decision === 'pending' && Number(i.approved_qty ?? i.suggested_qty) > 0
    && (Number(i.suggested_qty) > 0 || i.added_manually || i.in_customer_orders));
  const bothPending = run.items.filter((i: any) => isBoth(i) && i.decision === 'pending');
  const co = run.customer_orders_info;

  const addCustomerOrders = async () => {
    setSaving(true); setError(null);
    try { await orderProcessorAPI.addCustomerOrders(run.id, coFrom, coTo); await onChanged(); setSource('all'); setPage(0); }
    catch (e) { setError(errMsg(e)); } finally { setSaving(false); }
  };

  const acceptAllPending = () => {
    if (!window.confirm(`Accept all ${pendingToOrder.length} pending item(s) with their current quantities?`)) return;
    save(pendingToOrder.map((i: any) => ({ id: i.id, decision: 'accepted', approved_qty: Number(i.approved_qty ?? i.suggested_qty) })));
  };

  const finishReview = () => {
    if (pendingToOrder.length && !window.confirm(`${pendingToOrder.length} item(s) are still pending and will NOT be ordered. Continue?`)) return;
    onNext();
  };
  const acceptedValue = accepted.reduce((a: number, i: any) => a + Number(i.approved_qty || 0) * Number(i.landing_price || 0), 0);
  // New-store refills replace stock already sold and are outside the 85% budget
  const isRefill = (i: any) => i.category_label === '🆕 New Store Refill';
  const acceptedRefill = accepted.filter(isRefill).reduce((a: number, i: any) => a + Number(i.approved_qty || 0) * Number(i.landing_price || 0), 0);
  const overBudget = acceptedValue - acceptedRefill > Number(budget.budget_85 || Infinity);
  const newStore = budget.new_store;
  const suggestedValue = run.items.filter((i: any) => Number(i.suggested_qty) > 0)
    .reduce((a: number, i: any) => a + Number(i.suggested_qty) * Number(i.landing_price || 0), 0);

  const save = async (updates: any[]) => {
    setSaving(true); setError(null);
    try {
      await orderProcessorAPI.updateItems(run.id, updates);
      await onChanged();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const bulk = (d: 'accepted' | 'rejected') => {
    const ids = Array.from(selected);
    const updates = ids.map((id) => {
      const it = run.items.find((x: any) => x.id === id);
      const qty = Number(it?.approved_qty) > 0 ? Number(it.approved_qty) : Number(it?.suggested_qty) > 0 ? Number(it.suggested_qty) : undefined;
      return d === 'accepted' ? { id, decision: d, approved_qty: qty } : { id, decision: d, reject_reason: rejectReason || undefined };
    }).filter((u) => u.decision === 'rejected' || (u.approved_qty && u.approved_qty > 0));
    if (!updates.length) { setError('Selected items need a quantity before they can be accepted.'); return; }
    save(updates).then(() => setSelected(new Set()));
  };

  const commitQty = (item: any) => {
    const raw = qtyDraft[item.id];
    if (raw === undefined) return;
    const q = Number(raw);
    setQtyDraft((d) => { const n = { ...d }; delete n[item.id]; return n; });
    if (!Number.isFinite(q) || q < 0 || String(q) === String(Number(item.approved_qty))) return;
    save([{ id: item.id, approved_qty: q, ...(q === 0 ? { decision: 'rejected', reject_reason: 'qty set to 0' } : {}) }]);
  };

  const toggle = (id: number) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allOnPage = pageRows.length > 0 && pageRows.every((r: any) => selected.has(r.id));

  const exportCsv = async (kind: 'suggestions' | 'remaining' | 'reviewed') => {
    try { downloadBlob(await orderProcessorAPI.exportRunCsv(run.id, kind), `${run.run_number}_${kind}.csv`); } catch (e) { setError(errMsg(e)); }
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {run.source === 'optimizer' && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} divider={<Box sx={{ borderLeft: 1, borderColor: 'divider' }} />}>
            <Box>
              <Typography variant="caption" color="text.secondary">85% order budget</Typography>
              <Typography variant="h6">{inr(budget.budget_85)}</Typography>
              <Typography variant="caption">window {(budget.dates || []).slice().reverse().join(', ')}{budget.floor_applied ? ' · 7-day sales floor applied' : ''}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Rules suggested</Typography>
              <Typography variant="h6">{run.suggested_items} items · {inr(suggestedValue)}</Typography>
              <Typography variant="caption">before budget {inr(budget.original_total_cost)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Accepted now</Typography>
              <Typography variant="h6" color={overBudget ? 'warning.main' : 'success.main'}>
                {accepted.length} items · {inr(acceptedValue)}
              </Typography>
              <Typography variant="caption">{overBudget ? 'above the 85% budget' : 'within budget'}{acceptedRefill > 0 ? ` · incl. ${inr(acceptedRefill)} refill` : ''}</Typography>
            </Box>
            {newStore?.active && (
              <Box>
                <Typography variant="caption" color="text.secondary">🆕 New store refill (outside budget)</Typography>
                <Typography variant="h6">{newStore.refill_items ?? 0} items · {inr(newStore.refill_cost)}</Typography>
                <Typography variant="caption">replaces what sold in 30 days · opened {newStore.opened_on} · until {newStore.until}</Typography>
              </Box>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary">Min/Max</Typography>
              <Typography variant="body2">{run.minmax_stats?.updated ?? 0} forced · {run.minmax_stats?.deactivated ?? 0} deactivated</Typography>
              <Typography variant="caption">rule version {run.rule_version}</Typography>
            </Box>
          </Stack>
        </Paper>
      )}

      {run.source === 'optimizer' && (
        <Box sx={{ mb: 2 }}>
          {[
            { title: `Items reduced/zeroed by the 85% budget (${run.budget_trimmed?.length || 0})`, rows: budgetTrimmedRows, cols: ['medicine_name', 'manufacturer_name', 'Pack', 'Current Stock', 'Avg/day', 'Days of Stock', 'Category Label', 'Original Suggested Quantity', 'Budget-Capped Quantity'] },
            { title: `Min/Max items skipped — no sales in 182 days or already covered (${run.skipped_minmax?.length || 0})`, rows: skippedMinmaxRows, cols: ['medicine_name', 'manufacturer_name', 'Pack', 'min', 'max', 'Current Stock', 'Avg/day'] },
            { title: `Duplicate master entries consolidated (${run.merged_twins?.length || 0})`, rows: run.merged_twins || [], cols: ['manufacturer_name', 'medicine_name', 'member_ids', 'canonical_id', 'Current Stock', 'avg_daily_sales', 'Suggested Quantity'] },
            { title: `Inventory rows not matched to a medicine master (${run.unmatched_rows?.length || 0})`, rows: run.unmatched_rows || [], cols: ['manufacturer_name', 'medicine_name', 'Current Stock'] },
          ].map((p) => (
            <Accordion key={p.title} disableGutters variant="outlined" TransitionProps={{ unmountOnExit: true }}>
              <AccordionSummary expandIcon={<ExpandMore />}><Typography variant="body2">{p.title}</Typography></AccordionSummary>
              <AccordionDetails>
                {p.rows.length ? <PanelTable rows={p.rows} cols={p.cols} /> : <Typography variant="body2" color="text.secondary">None</Typography>}
                {p.title.startsWith('Min/Max') && p.rows.length > 0 && (
                  <Typography variant="caption" color="text.secondary">To order one anyway: turn on “Show all inventory items”, search it, set a quantity.</Typography>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {run.source === 'optimizer' && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} flexWrap="wrap" useFlexGap>
            <Box sx={{ flexGrow: 1, minWidth: 260 }}>
              <Typography variant="subtitle2">Include customer orders (optional)</Typography>
              {co ? (
                <Typography variant="caption" color="text.secondary">
                  {co.date_from} → {co.date_to}: {co.orders_included} order(s) · {co.items_both} item(s) also suggested by the optimizer
                  · {co.items_customer_only} customer-only · synced {fmtDateTime(co.synced_at)}
                  {co.orders_on_earlier_po?.length ? ` · ${co.orders_on_earlier_po.length} order(s) already on an earlier PO — flagged “on PO”, check before ordering again` : ''}
                  {co.orders_excluded_on_po?.length ? ` · ${co.orders_excluded_on_po.length} order(s) skipped (already on a PO)` : ''}
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Pending customer orders (quantities in strips) are merged into this run; items in both are highlighted for a total-quantity decision.
                </Typography>
              )}
              {co?.in_other_open_runs?.length > 0 && (
                <Typography variant="caption" color="warning.main" display="block">
                  Some of these orders are also in open run(s) {co.in_other_open_runs.join(', ')} — cancel those to avoid ordering twice.
                </Typography>
              )}
            </Box>
            {editable && (
              <>
                <TextField size="small" type="date" label="From" value={coFrom} onChange={(e) => setCoFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
                <TextField size="small" type="date" label="To" value={coTo} onChange={(e) => setCoTo(e.target.value)} InputLabelProps={{ shrink: true }} />
                <Button variant="outlined" startIcon={<GroupAdd />} disabled={saving} onClick={addCustomerOrders}>
                  {co ? 'Re-sync customer orders' : 'Add customer orders'}
                </Button>
              </>
            )}
          </Stack>
        </Paper>
      )}

      {editable && bothPending.length > 0 && (
        <Alert severity="info" sx={{ mb: 1.5 }} action={<Button color="inherit" size="small" onClick={() => { setSource('both'); setPage(0); }}>Show them</Button>}>
          {bothPending.length} item(s) are suggested by the optimizer AND ordered by customers — order qty is prefilled as
          optimizer + customer units; confirm or change the total, then Accept.
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
        <TextField size="small" label="Search medicine / manufacturer" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} sx={{ minWidth: 260 }} />
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Category</InputLabel>
          <Select label="Category" value={category} onChange={(e) => { setCategory(e.target.value); setPage(0); }}>
            <MenuItem value="all">All categories</MenuItem>
            {categories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Decision</InputLabel>
          <Select label="Decision" value={decision} onChange={(e) => { setDecision(e.target.value); setPage(0); }}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="accepted">Accepted</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Source</InputLabel>
          <Select label="Source" value={source} onChange={(e) => { setSource(e.target.value as any); setPage(0); }}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="optimizer">Optimizer</MenuItem>
            <MenuItem value="customer">Customer orders</MenuItem>
            <MenuItem value="both">In both</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel control={<Switch checked={showAll} onChange={(e) => { setShowAll(e.target.checked); setPage(0); }} />} label="Show all inventory items" />
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" startIcon={<Download />} onClick={() => exportCsv(run.source === 'optimizer' ? 'suggestions' : 'reviewed')}>Suggestions CSV</Button>
        {run.source === 'optimizer' && <Button size="small" startIcon={<Download />} onClick={() => exportCsv('remaining')}>Remaining CSV</Button>}
        <Button size="small" startIcon={<Download />} onClick={() => exportCsv('reviewed')}>Reviewed CSV</Button>
      </Stack>

      {editable && pendingToOrder.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1.5 }} action={<Button color="inherit" size="small" onClick={acceptAllPending} disabled={saving}>Accept all pending</Button>}>
          {pendingToOrder.length} item(s) waiting for a decision — select and Accept/Reject, or set a quantity.
        </Alert>
      )}

      {editable && selected.size > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="body2"><b>{selected.size}</b> selected</Typography>
          <Button size="small" variant="contained" color="success" startIcon={<Check />} disabled={saving} onClick={() => bulk('accepted')}>Accept</Button>
          <TextField size="small" placeholder="Reject reason (optional)" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
          <Button size="small" variant="contained" color="error" startIcon={<Close />} disabled={saving} onClick={() => bulk('rejected')}>Reject</Button>
          <Button size="small" onClick={() => setSelected(new Set())}>Clear</Button>
        </Paper>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '60vh' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {editable && (
                <TableCell padding="checkbox">
                  <Checkbox size="small" checked={allOnPage} indeterminate={!allOnPage && pageRows.some((r: any) => selected.has(r.id))}
                    onChange={() => setSelected((s) => { const n = new Set(s); pageRows.forEach((r: any) => (allOnPage ? n.delete(r.id) : n.add(r.id))); return n; })} />
                </TableCell>
              )}
              <SortHead k="medicine" label="Medicine" />
              <SortHead k="pack" label="Pack" />
              <SortHead k="category" label="Category" />
              <SortHead k="stock" label="Stock" align="right" />
              <SortHead k="avg" label="Avg/day" align="right" />
              <SortHead k="dos" label="Days stock" align="right" />
              <SortHead k="bills" label="Bills 84d" align="right" />
              <SortHead k="landing" label="Landing" align="right" />
              <SortHead k="optimizer" label={<Tooltip title="Optimizer suggestion in loose units (tablets / capsules / bottles)"><span>Optimizer (units)</span></Tooltip>} align="right" />
              <SortHead k="customer" label={<Tooltip title="Customer orders as entered (strips / packs) → converted to loose units with the pack size"><span>Customer orders (strips → units)</span></Tooltip>} align="right" />
              <SortHead k="order" label={<Tooltip title="Total to order in loose units = optimizer units + customer units. The PO sends it as strips (shown below the qty)."><span>Order qty (units)</span></Tooltip>} align="right" minWidth={110} />
              <SortHead k="decision" label="Decision" />
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.map((i: any) => (
              <TableRow key={i.id} hover selected={selected.has(i.id)}
                sx={{ opacity: i.decision === 'rejected' ? 0.55 : 1, bgcolor: isBoth(i) && !selected.has(i.id) ? '#fff8e1' : undefined }}>
                {editable && <TableCell padding="checkbox"><Checkbox size="small" checked={selected.has(i.id)} onChange={() => toggle(i.id)} /></TableCell>}
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{i.medicine_name}</Typography>
                  <Typography variant="caption" color="text.secondary">{i.manufacturer_name}</Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{i.pack_size && i.pack_size !== 'nan' ? i.pack_size : '—'}</TableCell>
                <TableCell>
                  <Typography variant="body2">{i.category_label}</Typography>
                  <Stack direction="row" spacing={0.5}>
                    {i.minmax_forced && <Chip size="small" variant="outlined" label={`min/max ${num(i.minmax_min, 0)}-${num(i.minmax_max, 0)}`} />}
                    {i.budget_trimmed && <Tooltip title={`Rules wanted ${num(i.pre_budget_qty, 0)}; cut by the 85% budget`}><Chip size="small" color="warning" variant="outlined" label="budget cut" /></Tooltip>}
                    {i.is_first_time_recent && <Chip size="small" variant="outlined" label="new item" />}
                    {i.added_manually && <Chip size="small" variant="outlined" label="manual" />}
                    {isBoth(i) && <Chip size="small" color="warning" label="Optimizer + Customer" />}
                    {i.in_customer_orders && !isBoth(i) && <Chip size="small" color="info" variant="outlined" label="customer order" />}
                    {i.in_customer_orders && co?.items_on_earlier_po?.[i.consol_key]?.length > 0 && (
                      <Tooltip title={`These customer orders are already on ${co.items_on_earlier_po[i.consol_key].join(', ')} (not cancelled, not marked “not supplied”). They are still pending because no one marked them ordered — order again only if that PO won’t deliver.`}>
                        <Chip size="small" color="warning" variant="outlined" label={`on PO ${co.items_on_earlier_po[i.consol_key][0]}${co.items_on_earlier_po[i.consol_key].length > 1 ? ' +' : ''}`} />
                      </Tooltip>
                    )}
                    {i.in_customer_orders && Number(i.current_stock) >= Number(i.customer_qty_units) && Number(i.customer_qty_units) > 0 && (
                      <Tooltip title={`Current stock ${num(i.current_stock, 0)} already covers the customer order (${num(i.customer_qty_units, 0)} units) — order only if you also want to restock`}>
                        <Chip size="small" color="success" variant="outlined" label="in stock" />
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
                <TableCell align="right">{num(i.current_stock, 0)}</TableCell>
                <TableCell align="right">{num(i.avg_daily_sales)}</TableCell>
                <TableCell align="right">{num(i.days_of_stock, 1)}</TableCell>
                <TableCell align="right">{num(i.bill_count, 0)}</TableCell>
                <TableCell align="right">{num(i.landing_price)}</TableCell>
                <TableCell align="right">{i.in_optimizer ? num(i.optimizer_qty ?? i.suggested_qty, 0) : '—'}</TableCell>
                <TableCell align="right">
                  {i.in_customer_orders ? (
                    <Tooltip title={`${num(i.customer_order_count, 0)} customer order(s); strips × pack size = units`}>
                      {packCount(i.pack_size)
                        ? <span>{num(i.customer_qty_strips, 0)} str → <b>{num(i.customer_qty_units, 0)}</b></span>
                        : <span><b>{num(i.customer_qty_units, 0)}</b> pack{Number(i.customer_qty_units) === 1 ? '' : 's'}</span>}
                    </Tooltip>
                  ) : '—'}
                </TableCell>
                <TableCell align="right">
                  {editable ? (
                    <TextField size="small" type="number" inputProps={{ min: 0, style: { textAlign: 'right', width: 60 } }}
                      value={qtyDraft[i.id] ?? (i.approved_qty ?? '')}
                      onChange={(e) => setQtyDraft((d) => ({ ...d, [i.id]: e.target.value }))}
                      onBlur={() => commitQty(i)}
                      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
                  ) : num(i.approved_qty, 0)}
                  {(() => {
                    const st = stripsFor(qtyDraft[i.id] ?? i.approved_qty, i.pack_size);
                    return st !== null && Number(qtyDraft[i.id] ?? i.approved_qty) > 0
                      ? <Typography variant="caption" display="block" color="text.secondary">= {st} strip{st === 1 ? '' : 's'}</Typography>
                      : null;
                  })()}
                </TableCell>
                <TableCell>
                  <StatusChip status={i.decision} />
                  {i.reject_reason && <Typography variant="caption" display="block" color="text.secondary">{i.reject_reason}</Typography>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination component="div" count={filtered.length} page={page} rowsPerPage={rowsPerPage}
        onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[25, 50, 100, 250]} />

      {editable && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button variant="contained" disabled={!accepted.length || saving} onClick={finishReview}>
            Review done — assign distributors ({accepted.length} items)
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ReviewStep;
